// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Loader2, 
  ArrowLeft, 
  Download, 
  User, 
  Calendar,
  Package,
  FileImage,
  MessageSquare,
  Send,
  Upload,
  CheckCircle
} from 'lucide-react';
import ImageUpload from '@/components/vendor-graphics/ImageUpload';

interface GraphicsOrder {
  id: string;
  status: string;
  total_amount: number;
  created_at: string;
  requirements: any;
  completed_files: string[];
  athlete_id: string;
  graphics_packages?: {
    name: string;
  } | null;
  athlete?: {
    name: string;
    email: string;
  } | null;
}

interface GraphicsMessage {
  id: string;
  sender_id: string;
  message: string;
  attachments: string[];
  created_at: string;
}

const VendorOrderDetail: React.FC = () => {
  console.log("🚀 VendorOrderDetail component mounted");
  const { orderId } = useParams<{ orderId: string }>();
  console.log("📝 Order ID from params:", orderId);
  const navigate = useNavigate();
  const { user } = useAuth();
  console.log("👤 Current user:", user?.id);
  const [order, setOrder] = useState<GraphicsOrder | null>(null);
  const [messages, setMessages] = useState<GraphicsMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [proofImages, setProofImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    console.log("🔄 useEffect triggered, orderId:", orderId, "user:", user?.id);
    if (orderId) {
      console.log("📊 Starting to fetch order and messages");
      fetchOrder();
      fetchMessages();
    } else {
      console.log("❌ No orderId provided, setting loading to false");
      setLoading(false);
    }
  }, [orderId]);

  // Real-time subscription for messages
  useEffect(() => {
    if (!orderId) return;

    const channel = supabase
      .channel(`order-messages-${orderId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'graphics_messages',
        filter: `order_id=eq.${orderId}`
      }, () => {
        console.log('🔄 New message received, refreshing messages');
        fetchMessages();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orderId]);

  const fetchOrder = async () => {
    console.log("🔍 fetchOrder called for orderId:", orderId);
    try {
      // First get vendor profile to verify access
      console.log("👤 Getting vendor profile for user:", user?.id);
      const { data: vendorProfile, error: vendorError } = await supabase
        .from('graphics_vendor_profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (vendorError) {
        console.error("❌ Error fetching vendor profile:", vendorError);
        throw vendorError;
      }
      
      console.log("✅ Vendor profile found:", vendorProfile);

      const { data, error } = await supabase
        .from('graphics_orders')
        .select(`
          id,
          status,
          total_amount,
          created_at,
          requirements,
          completed_files,
          athlete_id,
          graphics_packages!graphics_orders_package_id_fkey(name),
          athlete:profiles!graphics_orders_athlete_id_fkey(name, email)
        `)
        .eq('id', orderId)
        .eq('vendor_id', vendorProfile.id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Order not found');

      // Transform the data to handle single objects instead of arrays
      const transformedOrder = {
        ...data,
        graphics_packages: Array.isArray(data.graphics_packages) 
          ? data.graphics_packages[0] || null 
          : data.graphics_packages,
        athlete: Array.isArray(data.athlete)
          ? data.athlete[0] || null
          : data.athlete
      };

      setOrder(transformedOrder);
      setProofImages(transformedOrder.completed_files || []);
      console.log("✅ Order data set successfully:", transformedOrder);
    } catch (error) {
      console.error('❌ Error fetching order:', error);
      toast.error('Failed to load order details');
      navigate('/vendor-graphics-dashboard/orders');
    } finally {
      console.log("🏁 fetchOrder completed, setting loading to false");
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      console.log('🔍 Fetching messages for order:', orderId);
      
      const { data, error } = await supabase
        .from('graphics_messages')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      console.log('📧 Messages fetched:', data);

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const downloadImage = (imageUrl: string, index: number) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `reference-image-${index + 1}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    try {
      // Get vendor's profile ID from their main profile
      const { data: vendorProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (!vendorProfile) {
        toast.error('Profile not found');
        return;
      }

      console.log('Sending message with profile ID:', vendorProfile.id);

      // Insert message with explicit column list
      const { data, error } = await supabase
        .from('graphics_messages')
        .insert([{
          order_id: orderId,
          sender_id: vendorProfile.id, // Use the vendor's profile ID
          message: newMessage.trim(),
          attachments: [],
          created_at: new Date().toISOString()
        }])
        .select();

      if (error) throw error;

      // Send notifications to athlete
      await sendAthleteNotifications();
      
      setNewMessage('');
      await fetchMessages();
      toast.success('Message sent');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(`Failed to send message: ${error.message}`);
    }
  };

  const sendAthleteNotifications = async () => {
    if (!order?.athlete_id) return;
    
    try {
      console.log('🔔 Sending notifications to athlete:', order.athlete_id);
      
      // Get athlete's contact info
      const { data: athleteProfile } = await supabase
        .from('profiles')
        .select('email, phone, name')
        .eq('id', order.athlete_id)
        .single();

      if (!athleteProfile) {
        console.log('⚠️ Athlete profile not found');
        return;
      }

      console.log('📧 Athlete contact info:', athleteProfile.email, athleteProfile.phone);

      // Queue SMS notification
      if (athleteProfile.phone) {
        const { error: smsError } = await supabase.from('communication_logs').insert({
          user_id: order.athlete_id,
          type: 'sms',
          recipient_phone: athleteProfile.phone,
          content: `New message from your graphics vendor regarding your order. Check your RecruitLook dashboard to respond.`,
          status: 'pending',
          metadata: {
            trigger_source: 'vendor_message',
            order_id: orderId
          }
        });
        
        if (smsError) {
          console.error('Error queuing SMS:', smsError);
        } else {
          console.log('✅ SMS notification queued');
        }
      }

      // Queue email notification
      if (athleteProfile.email) {
        const { error: emailError } = await supabase.from('communication_logs').insert({
          user_id: order.athlete_id,
          type: 'email',
          recipient_email: athleteProfile.email,
          subject: '📨 New Message About Your Graphics Order',
          content: `Hi ${athleteProfile.name},\n\nYour graphics vendor has sent you a message about your order.\n\nMessage: "${newMessage}"\n\nPlease log in to your RecruitLook dashboard to view and respond.\n\nBest regards,\nThe RecruitLook Team`,
          status: 'pending',
          metadata: {
            trigger_source: 'vendor_message',
            order_id: orderId,
            order_amount: order.total_amount
          }
        });
        
        if (emailError) {
          console.error('Error queuing email:', emailError);
        } else {
          console.log('✅ Email notification queued');
        }
      }
    } catch (error) {
      console.error('Error sending notifications:', error);
    }
  };

  const handleProofUpload = async () => {
    if (proofImages.length === 0) {
      toast.error('Please upload at least one proof image');
      return;
    }

    setUploading(true);
    try {
      // Get vendor profile
      const { data: vendorProfile } = await supabase
        .from('graphics_vendor_profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (!vendorProfile) {
        throw new Error('Vendor profile not found');
      }

      // Update order with completed files and status
      const { error: updateError } = await supabase
        .from('graphics_orders')
        .update({
          completed_files: proofImages,
          status: 'ready_for_review'
        })
        .eq('id', orderId)
        .eq('vendor_id', vendorProfile.id);

      if (updateError) throw updateError;

      // Send notification to athlete
      await sendProofNotification();
      
      // Refresh order data
      await fetchOrder();
      
      toast.success('Proofs uploaded successfully! Athlete has been notified.');
    } catch (error) {
      console.error('Error uploading proofs:', error);
      toast.error(`Failed to upload proofs: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const sendProofNotification = async () => {
    if (!order?.athlete_id) return;
    
    try {
      // Get athlete's contact info
      const { data: athleteProfile } = await supabase
        .from('profiles')
        .select('email, phone, name')
        .eq('id', order.athlete_id)
        .single();

      if (!athleteProfile) return;

      const packageName = order.graphics_packages?.name || 'Custom Package';

      // Queue SMS notification
      if (athleteProfile.phone) {
        await supabase.from('communication_logs').insert({
          user_id: order.athlete_id,
          type: 'sms',
          recipient_phone: athleteProfile.phone,
          content: `🎨 Great news! Your graphics proofs for "${packageName}" are ready for review. Check your RecruitLook dashboard to view and approve them.`,
          status: 'pending',
          metadata: {
            trigger_source: 'vendor_proofs_ready',
            order_id: orderId
          }
        });
      }

      // Queue email notification
      if (athleteProfile.email) {
        await supabase.from('communication_logs').insert({
          user_id: order.athlete_id,
          type: 'email',
          recipient_email: athleteProfile.email,
          subject: '🎨 Your Graphics Proofs Are Ready!',
          content: `Hi ${athleteProfile.name},\n\nExciting news! Your graphics vendor has completed your "${packageName}" order and uploaded the proofs for your review.\n\nYou can now:\n• View all proof images\n• Request revisions if needed\n• Approve and download final graphics\n\nLog in to your RecruitLook dashboard to review your proofs.\n\nBest regards,\nThe RecruitLook Team`,
          status: 'pending',
          metadata: {
            trigger_source: 'vendor_proofs_ready',
            order_id: orderId,
            order_amount: order.total_amount
          }
        });
      }
    } catch (error) {
      console.error('Error sending proof notifications:', error);
    }
  };

  const getSenderName = (senderId: string) => {
    if (senderId === order?.athlete_id) {
      return order.athlete?.name || 'Athlete';
    }
    // Check if it's the current vendor's profile ID
    return 'You'; // Vendor's message
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading order details...</span>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-semibold mb-2">Order Not Found</h3>
        <p className="text-muted-foreground mb-4">
          The order you're looking for doesn't exist or you don't have access to it.
        </p>
        <Button onClick={() => navigate('/vendor-graphics-dashboard/orders')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>
      </div>
    );
  }

  // Get all reference images from messages
  const allReferenceImages = messages.flatMap(msg => msg.attachments || []);

  console.log("🎨 About to render component - order:", !!order, "messages:", messages.length, "allReferenceImages:", allReferenceImages.length);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate('/vendor-graphics-dashboard/orders')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Orders
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Order Details</h1>
          <p className="text-muted-foreground">
            Complete project information and requirements
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Requirements */}
          {order.requirements && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Project Requirements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">School Name</label>
                    <div className="text-base">{order.requirements.school_name || 'Not provided'}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Athlete Name</label>
                    <div className="text-base">{order.requirements.athlete_name || 'Not provided'}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Graduation Year</label>
                    <div className="text-base">{order.requirements.graduation_year || 'Not provided'}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Sport</label>
                    <div className="text-base">{order.requirements.sport || 'Not provided'}</div>
                  </div>
                  {order.requirements.position && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Position</label>
                      <div className="text-base">{order.requirements.position}</div>
                    </div>
                  )}
                </div>
                {order.requirements.special_instructions && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Special Instructions</label>
                    <div className="text-base bg-muted p-3 rounded-lg mt-1">
                      {order.requirements.special_instructions}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Reference Images */}
          {allReferenceImages.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileImage className="h-5 w-5" />
                  Reference Images ({allReferenceImages.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {allReferenceImages.map((imageUrl, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={imageUrl}
                        alt={`Reference ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:opacity-75 transition-opacity"
                        onClick={() => window.open(imageUrl, '_blank')}
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadImage(imageUrl, index);
                          }}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Proof Upload Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Proofs
                {order.status === 'ready_for_review' && (
                  <Badge variant="secondary" className="ml-2">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Proofs Uploaded
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Upload completed graphics for the athlete to review. They'll be notified once you submit the proofs.
              </div>
              
              <ImageUpload
                images={proofImages}
                onImagesChange={setProofImages}
                maxImages={10}
                className="w-full"
              />
              
              {proofImages.length > 0 && (
                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {proofImages.length} proof image{proofImages.length !== 1 ? 's' : ''} ready to upload
                  </div>
                  <Button 
                    onClick={handleProofUpload}
                    disabled={uploading || proofImages.length === 0}
                    className="min-w-[120px]"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {order.status === 'ready_for_review' ? 'Update Proofs' : 'Submit Proofs'}
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {order.status === 'ready_for_review' && order.completed_files?.length > 0 && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800 font-medium mb-2">
                    <CheckCircle className="h-4 w-4" />
                    Proofs Successfully Submitted
                  </div>
                  <div className="text-sm text-green-700">
                    You've uploaded {order.completed_files.length} proof image{order.completed_files.length !== 1 ? 's' : ''}. 
                    The athlete has been notified and can now review your work.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Messages Thread */}
          <Card className="h-[600px] flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Messages
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col">
              {/* Messages List */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4">
                {messages.map((message) => {
                  const isVendorMessage = message.sender_id !== order?.athlete_id;
                  const senderName = getSenderName(message.sender_id);
                  
                  return (
                    <div key={message.id} className={`flex ${isVendorMessage ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-lg p-3 ${
                        isVendorMessage 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted'
                      }`}>
                        <div className="text-xs font-semibold mb-1 opacity-70">
                          {senderName}
                        </div>
                        <div className="text-sm">{message.message}</div>
                        <div className="text-xs opacity-70 mt-1">
                          {new Date(message.created_at).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Show uploaded images count */}
                {allReferenceImages.length > 0 && (
                  <div className="flex justify-start">
                    <div className="bg-blue-900 text-white rounded-lg p-3 max-w-[80%]">
                      <div className="text-sm">📎 Uploaded {allReferenceImages.length} reference images</div>
                      <div className="text-xs mt-1">
                        {allReferenceImages.map((_, index) => (
                          <div key={index}>Attachment {index + 1}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Message Input */}
              <div className="flex gap-2">
                <Input 
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                />
                <Button onClick={sendMessage}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Order Info */}
          <Card>
            <CardHeader>
              <CardTitle>Order Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge variant="default">
                  {order.status === 'pending_info' ? 'Awaiting Requirements' :
                   order.status === 'in_progress' ? 'In Progress' :
                   order.status === 'ready_for_review' ? 'Ready for Review' :
                   'Completed'}
                </Badge>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">{order.athlete?.name}</div>
                    <div className="text-xs text-muted-foreground">{order.athlete?.email}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">{order.graphics_packages?.name || 'Custom Package'}</div>
                    <div className="text-xs text-muted-foreground">${order.total_amount}</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm font-medium">Order Date</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(order.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </div>
  );
};

export default VendorOrderDetail;